'use client'

import type { ColumnDef } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactTimeAgo from 'react-time-ago'

import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import { Avatar, AvatarFallback, AvatarImage, Checkbox, DataTableColumnHeader } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student, StudentAction, StudentColumnId } from './types'
import { useSharedTRPC } from '../../context'
import { AccessCell } from './cells/access-cell'
import { ClassCell, getSortedSchoolClasses } from './cells/class-cell'
import { CopyPinMenu } from './cells/copy-pin-menu'
import { GradeCell } from './cells/grade-cell'
import { ParentCell } from './cells/parent-cell'
import { EditStudentButton, RowActions } from './row-actions'
import { getStudentAccess, NO_SCHOOL_GRADE_ID } from './types'

/**
 * "Aktif 1 menit lalu" while the visit is recent, "Aktif 12 Januari 2026" once it is not — a
 * relative age stops being readable somewhere past a week ("aktif 8 bulan lalu" tells a teacher
 * less than the date does).
 */
function LastActive({ lastActive }: { lastActive: string | null }) {
  if (!lastActive) {
    return (
      <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>Belum pernah aktif</span>
    )
  }

  const date = new Date(lastActive)

  return (
    <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}>
      Aktif <ReactTimeAgo date={date} locale='id-ID' />
    </span>
  )
}

/**
 * Renders the student's name inside the first column.
 *
 * A render prop because `@ui/shared` has no route tree of its own: the host app decides whether the
 * name is a link and which router draws it. `className` carries the shared type scale so every
 * roster's name column reads the same; add the link affordance (`hover:underline`) on top of it.
 */
export type RenderStudentName = (args: { student: Student; className: string }) => ReactNode

const NAME_CLASS_NAME = 'srd:text-neutral-black srd:w-fit'

/** Plain-text header cell — used by every column that is not itself sortable. */
function StaticHeader({ title }: { title: string }) {
  return (
    <div className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black srd:text-nowrap')}>
      {title}
    </div>
  )
}

export interface UseStudentColumnsProps {
  /** Drives copy that differs between a school roster and a family's own children. */
  type?: 'web' | 'school'
  canEdit: boolean
  /**
   * Governs the inline Kelas editor alone. Defaults to `canEdit`. Split out because that cell writes
   * `setChildTeams`, which stays owner/admin — a guru kelas edits their students but not which class
   * a student belongs to.
   */
  canEditClass?: boolean
  /**
   * `menu` is the full kebab (settings / premium seat / remove from school). `settings` is a single
   * edit button that opens the account sheet and nothing else — for rosters where the school-level
   * verbs do not belong.
   */
  rowAction?: 'menu' | 'settings'
  /** `menu` (default) offers the WhatsApp message too; `copy` is a one-click PIN copy. */
  pinAction?: 'menu' | 'copy'
  /** Which data columns to build, in the order they should appear. */
  columnIds: StudentColumnId[]
  enableRowSelection: boolean
  renderStudentName?: RenderStudentName
  /**
   * Right-hand action cell. Omit to get the default kebab (settings / premium seat / remove from
   * school), which drives `onAction`. Return `null` for a table with no row actions at all.
   */
  renderRowActions?: (student: Student) => ReactNode
  onAction: (action: StudentAction) => void
}

export function useStudentColumns({
  type,
  canEdit,
  canEditClass = canEdit,
  rowAction = 'menu',
  pinAction = 'menu',
  columnIds,
  enableRowSelection,
  renderStudentName,
  renderRowActions,
  onAction,
}: UseStudentColumnsProps): ColumnDef<Student>[] {
  const trpc = useSharedTRPC()

  // Premium seats are pooled across the organization, so the count is read once here and shared by
  // every row menu — asking per row would be one request per student on the roster.
  //
  // OWNED seats, across every pool. A seat another organization provisioned for a child both of them
  // share cannot be handed out here, so it must not be counted here either; `getActiveSeats` is
  // organization-scoped for exactly that reason. Unassigned only — `childId === null` — because a
  // seat already on a child is not available to give.
  const { data: premiums } = useQuery(trpc.myOrganization.getActiveSeats.queryOptions())
  const availableSeats = (premiums ?? []).filter((premium) => premium.childId === null).length

  const byId: Record<StudentColumnId, ColumnDef<Student>> = {
    name: {
      id: 'name',
      accessorKey: 'name',
      enableHiding: false,
      enableSorting: true,
      sortingFn: (a, b) => a.original.name.localeCompare(b.original.name),
      header: ({ column }) => <DataTableColumnHeader column={column} title={type === 'web' ? 'Anak' : 'Murid'} />,
      cell: ({ row }) => (
        <div className='srd:flex srd:min-w-[200px] srd:items-center srd:gap-x-2 srd:text-nowrap'>
          <Avatar profile='user' size='lg'>
            <AvatarImage src={row.original.image ?? undefined} alt={row.original.name} />
            <AvatarFallback>{getInitials(row.original.name)}</AvatarFallback>
          </Avatar>

          <span className='srd:flex srd:flex-col'>
            {renderStudentName ? (
              renderStudentName({
                student: row.original,
                className: cn(typographyVariants({ variant: 'label-md' }), NAME_CLASS_NAME),
              })
            ) : (
              <span className={cn(typographyVariants({ variant: 'label-md' }), NAME_CLASS_NAME)}>
                {row.original.name}
              </span>
            )}
            <LastActive lastActive={row.original.lastActive} />
          </span>
        </div>
      ),
    },

    pin: {
      id: 'pin',
      accessorKey: 'pin',
      enableHiding: false,
      enableSorting: false,
      header: () => <StaticHeader title={type === 'web' ? 'PIN Anak' : 'PIN Murid'} />,
      cell: ({ row }) => (
        <div className='srd:flex srd:items-center srd:gap-x-2 srd:text-nowrap'>
          <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-neutral-black')}>
            {row.original.pin ?? '-'}
          </span>
          <CopyPinMenu
            studentId={row.original.id}
            studentName={row.original.name}
            pin={row.original.pin}
            variant={pinAction}
          />
        </div>
      ),
    },

    schoolClass: {
      id: 'schoolClass',
      accessorFn: (row) =>
        getSortedSchoolClasses(row)
          .map((schoolClass) => schoolClass.name)
          .join(' '),
      enableHiding: false,
      enableSorting: false,
      // Matched against every class the student sits in, not just the first one: a student in both
      // 3A and 3B has to appear under either filter.
      filterFn: (row, _columnId, filterValue: string) => {
        const classes = row.original.schoolClasses ?? []
        if (filterValue === NO_SCHOOL_CLASS_ID) return classes.length === 0
        return classes.some((schoolClass) => schoolClass.id === filterValue)
      },
      header: () => <StaticHeader title='Kelas' />,
      cell: ({ row }) => <ClassCell student={row.original} canEdit={canEditClass} />,
    },

    schoolGrade: {
      id: 'schoolGrade',
      accessorKey: 'schoolGrade',
      enableHiding: false,
      enableSorting: false,
      filterFn: (row, _columnId, filterValue: string) => {
        const grade = row.original.schoolGrade ?? null
        if (filterValue === NO_SCHOOL_GRADE_ID) return grade === null
        return grade === filterValue
      },
      header: () => <StaticHeader title='Tingkat' />,
      cell: ({ row }) => <GradeCell student={row.original} canEdit={canEdit} />,
    },

    access: {
      id: 'access',
      // `kind` is source-agnostic, so the accessor and the filter need no `type` — only the label
      // under the badge does.
      accessorFn: (row) => getStudentAccess(row).kind,
      enableHiding: false,
      enableSorting: false,
      filterFn: (row, _columnId, filterValue: string) => getStudentAccess(row.original).kind === filterValue,
      header: () => <StaticHeader title='Akses' />,
      cell: ({ row }) => <AccessCell student={row.original} type={type} />,
    },

    parent: {
      id: 'parent',
      accessorFn: (row) => row.parentConnection.status,
      enableHiding: false,
      enableSorting: false,
      // Explicit exact match. TanStack's default filter for a string accessor is a substring test,
      // and 'none' is a substring of nothing else here today — but 'pending' vs a future
      // 'pending-expired' would silently start matching both.
      //
      // Reads through `getValue` rather than `row.original.parentConnection`: reaching into the row
      // pulls `ParentConnection` into this hook's inferred return type, which TypeScript can then
      // only name via a path into `@package/api`'s dist (TS2742).
      filterFn: (row, columnId, filterValue: string) => row.getValue(columnId) === filterValue,
      header: () => <StaticHeader title='Orang tua' />,
      // The action lives in the cell rather than the kebab so the column works on rosters that
      // render `rowAction='settings'` and therefore have no kebab to hang a menu item on.
      cell: ({ row }) => <ParentCell student={row.original} canEdit={canEdit} onAction={onAction} />,
    },
  }

  const selectColumn: ColumnDef<Student> = {
    id: 'select',
    enableSorting: false,
    enableHiding: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={table.getIsSomePageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label='Pilih semua murid'
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={`Pilih ${row.original.name}`}
      />
    ),
  }

  const actionsColumn: ColumnDef<Student> = {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    cell: ({ row }) => (
      <div className='srd:flex srd:justify-end'>
        {renderRowActions ? (
          renderRowActions(row.original)
        ) : rowAction === 'settings' ? (
          <EditStudentButton student={row.original} canEdit={canEdit} onAction={onAction} />
        ) : (
          <RowActions
            student={row.original}
            type={type}
            canEdit={canEdit}
            availableSeats={availableSeats}
            onAction={onAction}
          />
        )}
      </div>
    ),
  }

  return [
    ...(enableRowSelection ? [selectColumn] : []),
    ...columnIds.map((id) => byId[id]),
    actionsColumn,
  ] satisfies ColumnDef<Student>[]
}
